import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SentimentSelector from '../../molecules/SentimentSelector';

const BulkSentimentDialog = ({
  open,
  selectedCount,
  sentiment,
  onSentimentChange,
  onManualApply,
  onAnalyzeWithAI,
  onClose,
  loading,
  aiEnabled
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="xs"
    PaperProps={{
      sx: {
        borderRadius: 2,
        maxWidth: 380,
        m: 2
      }
    }}
  >
    <DialogContent sx={{ pt: 2.5, pb: 2, px: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
        Sentimento
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        {selectedCount} artigo{selectedCount !== 1 ? 's' : ''} selecionado{selectedCount !== 1 ? 's' : ''}
      </Typography>

      <Box sx={{ mb: 2.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1.5, display: 'block' }}>
          Definir Manualmente
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
            <SentimentSelector value={sentiment} onChange={onSentimentChange} size="medium" />
          </Box>
          <Button
            onClick={onManualApply}
            variant="contained"
            size="small"
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              minWidth: 70
            }}
            disabled={loading}
          >
            {loading ? '...' : 'Aplicar'}
          </Button>
        </Box>
      </Box>

      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        my: 2
      }}>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        <Typography variant="caption" color="text.disabled">ou</Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
      </Box>

      <Box sx={{
        p: 2,
        borderRadius: 1.5,
        bgcolor: aiEnabled ? 'action.hover' : 'action.disabledBackground',
        border: '1px solid',
        borderColor: aiEnabled ? 'divider' : 'action.disabled'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: aiEnabled ? 'secondary.main' : 'text.disabled' }} />
          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: aiEnabled ? 'text.secondary' : 'text.disabled' }}>
            Análise com MIA
          </Typography>
        </Box>
        <Typography variant="body2" color={aiEnabled ? 'text.secondary' : 'text.disabled'} sx={{ mb: 1.5, fontSize: '0.8rem' }}>
          Detecta automaticamente o sentimento usando IA
        </Typography>
        {!aiEnabled ? (
          <Alert severity="warning" sx={{ py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
            Ative sua MIA para usar
          </Alert>
        ) : (
          <Button
            onClick={onAnalyzeWithAI}
            variant="outlined"
            color="secondary"
            fullWidth
            startIcon={<AutoAwesomeIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 500
            }}
            disabled={loading}
          >
            {loading ? 'Analisando...' : 'Analisar com IA'}
          </Button>
        )}
      </Box>
    </DialogContent>

    <DialogActions sx={{ px: 3, pb: 2, pt: 0 }}>
      <Button
        onClick={onClose}
        variant="text"
        fullWidth
        sx={{
          textTransform: 'none',
          fontWeight: 500,
          color: 'text.secondary'
        }}
        disabled={loading}
      >
        Cancelar
      </Button>
    </DialogActions>
  </Dialog>
);

BulkSentimentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  selectedCount: PropTypes.number.isRequired,
  sentiment: PropTypes.string.isRequired,
  onSentimentChange: PropTypes.func.isRequired,
  onManualApply: PropTypes.func.isRequired,
  onAnalyzeWithAI: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  aiEnabled: PropTypes.bool
};

BulkSentimentDialog.defaultProps = {
  loading: false,
  aiEnabled: false
};

export default BulkSentimentDialog;
